import yellow1 from './images/bee_dice/yellow-1.svg';
import yellow2 from './images/bee_dice/yellow-2.svg';
import yellow3 from './images/bee_dice/yellow-3.svg';
import yellow4 from './images/bee_dice/yellow-4.svg';
import yellow5 from './images/bee_dice/yellow-5.svg';
import yellowWild from './images/bee_dice/yellow-wild.svg';
import white1 from './images/bee_dice/white-1.svg';
import white4 from './images/bee_dice/white-4.svg';
import white5 from './images/bee_dice/white-5.svg';
import white5Alt from './images/bee_dice/white-5-alt.svg';
import white6 from './images/bee_dice/white-6.svg';
import whiteWild from './images/bee_dice/white-wild.svg';

type BeeDieColor = 'white' | 'yellow';

type BeeDiceRoll = {
    white: number;
    yellow: number;
};

const whiteFaces = [white1, white4, white5, white5Alt, white6, whiteWild];
const yellowFaces = [yellow1, yellow2, yellow3, yellow4, yellow5, yellowWild];

const rollAnimationDelays = [34, 42, 52, 64, 78, 94, 116, 142, 174];

export class BeeDiceManager {
    private container: HTMLElement | null = null;
    private whiteDie: HTMLImageElement | null = null;
    private yellowDie: HTMLImageElement | null = null;
    private seed: string = '';
    private rolls: BeeDiceRoll[] = [];
    private enabled: boolean = false;
    private animationTimers: number[] = [];

    public initialize(): void {
        this.container = document.getElementById('bee-dice-row');
        this.whiteDie = document.getElementById('bee-die-white') as HTMLImageElement | null;
        this.yellowDie = document.getElementById('bee-die-yellow') as HTMLImageElement | null;
    }

    public setSeed(seed: string): void {
        this.seed = seed.toLocaleLowerCase();
        this.rolls = Array.from({ length: 12 }, (_, index) => ({
            white: this.rollDie('white', index + 1),
            yellow: this.rollDie('yellow', index + 1)
        }));
    }

    public setEnabled(isEnabled: boolean, currentRound: number): void {
        this.enabled = isEnabled;
        this.container?.classList.toggle('is-hidden', !isEnabled);
        window.dispatchEvent(new CustomEvent('card-row-visibility-change'));

        if (isEnabled) {
            this.showRound(currentRound, false);
        } else {
            this.clearAnimation();
        }
    }

    public showRound(round: number, animate: boolean): void {
        if (!this.enabled) {
            return;
        }

        const roll = this.rolls[Math.max(0, Math.min(round - 1, this.rolls.length - 1))];

        if (roll == null) {
            return;
        }

        if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.setDiceFaces(roll);
            this.playFinishAnimation();
            return;
        }

        this.animateToRoll(roll, round);
    }

    private animateToRoll(roll: BeeDiceRoll, round: number): void {
        this.clearAnimation();
        this.container?.classList.add('is-rolling');
        this.container?.classList.remove('has-finished-roll');

        let elapsed = 0;

        rollAnimationDelays.forEach((delay, index) => {
            elapsed += delay;
            const timer = window.setTimeout(() => {
                this.setDiceFaces({
                    white: (round + index) % whiteFaces.length,
                    yellow: (round + index + 2) % yellowFaces.length
                });
            }, elapsed);

            this.animationTimers.push(timer);
        });

        const finalTimer = window.setTimeout(() => {
            this.setDiceFaces(roll);
            this.container?.classList.remove('is-rolling');
            this.playFinishAnimation();
        }, elapsed + 120);

        this.animationTimers.push(finalTimer);
    }

    private setDiceFaces(roll: BeeDiceRoll): void {
        if (this.whiteDie != null) {
            this.whiteDie.src = whiteFaces[roll.white];
            this.whiteDie.alt = `White bee die ${this.getFaceLabel('white', roll.white)}`;
        }

        if (this.yellowDie != null) {
            this.yellowDie.src = yellowFaces[roll.yellow];
            this.yellowDie.alt = `Yellow bee die ${this.getFaceLabel('yellow', roll.yellow)}`;
        }
    }

    private playFinishAnimation(): void {
        this.container?.classList.remove('has-finished-roll');

        window.setTimeout(() => {
            this.container?.classList.add('has-finished-roll');
        }, 0);
    }

    private clearAnimation(): void {
        this.animationTimers.forEach((timer) => window.clearTimeout(timer));
        this.animationTimers = [];
        this.container?.classList.remove('is-rolling', 'has-finished-roll');
    }

    private rollDie(color: BeeDieColor, round: number): number {
        return this.hashToIndex(`${this.seed}|bee-dice|${color}|round-${round}`, 6);
    }

    private hashToIndex(value: string, size: number): number {
        let hash = 2166136261;

        for (let index = 0; index < value.length; index++) {
            hash ^= value.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }

        return (hash >>> 0) % size;
    }

    private getFaceLabel(color: BeeDieColor, index: number): string {
        const labels = color === 'white'
            ? ['1', '4', '5', '5', '6', 'wild']
            : ['1', '2', '3', '4', '5', 'wild'];

        return labels[index] ?? 'unknown';
    }
}
